import { Link } from 'react-router-dom';
import { selectedCases } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import SlotText from '@/components/feature/SlotText';
import ImageViewer, { isTextCover } from '@/components/feature/ImageViewer';

export default function SelectedCases() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="cases" className="py-20 md:py-28">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-14 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Selected Case Studies
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-heading fw-bold text-[clamp(32px,4.5vw,60px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                From Issue
                <br />
                <span className="text-foreground-500 fw-medium">to{' '}<SlotText text="Impact." as="span" className="fw-medium marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" /></span>
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-700 text-base leading-relaxed">
                「何を作ったか」よりも「何を解いたか」。各案件を、課題の構造化から戦略・実装・価値創出まで、一貫したCase Studyとして紹介しています。
              </p>
            </div>
          </div>
        </div>

        {/* Case grid */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 transition-all duration-1000 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {selectedCases.map((c) => (
            <Link
              key={c.id}
              to={`/case/${c.id}`}
              className="case-card group block will-change-transform"
              onMouseMove={(e) => {
                const el = e.currentTarget;
                const rect = el.getBoundingClientRect();
                const x = (e.clientX - rect.left) / rect.width - 0.5;
                const y = (e.clientY - rect.top) / rect.height - 0.5;
                el.style.transform = `perspective(1000px) rotateX(${(-y * 3).toFixed(2)}deg) rotateY(${(x * 3).toFixed(2)}deg)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
              }}
            >
              {/* Image */}
              <div className="relative overflow-hidden bg-background-200">
                <div className="w-full aspect-[4/3]">
                  {isTextCover(c.id) ? (
                    <ImageViewer
                      src={c.image}
                      alt={c.title}
                      className="w-full h-full"
                      controlsPosition="bottom-right"
                    />
                  ) : (
                    <img
                      src={c.image}
                      alt={c.title}
                      className="case-img w-full h-full object-cover object-top"
                    />
                  )}
                </div>
                <div className="flow-glow absolute inset-0" />
              </div>

              {/* Card info */}
              <div className="pt-5 pb-2 border-b border-background-300">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-label text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 bg-background-100 text-foreground-500">
                    {c.year}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300" />
                  <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
                    {c.category}
                  </p>
                </div>
                <h3 className="font-heading text-2xl md:text-[26px] leading-tight text-foreground-950 tracking-[-0.02em] mb-3 hover-colorful">
                  <SlotText text={c.title} scrambleColor="rainbow" />
                </h3>

                {/* Issue / Value */}
                <div className="grid grid-cols-1 gap-2 mt-3">
                  <div className="flex gap-3 items-start">
                    <span className="font-label text-[9px] tracking-[0.1em] uppercase text-foreground-500 mt-0.5 flex-shrink-0 w-10">
                      Issue
                    </span>
                    <p className="text-xs text-foreground-700 leading-relaxed">{c.issue}</p>
                  </div>
                  <div className="flex gap-3 items-start">
                    <span className="font-label text-[9px] tracking-[0.1em] uppercase text-accent-600 mt-0.5 flex-shrink-0 w-10">
                      Value
                    </span>
                    <p className="text-xs text-foreground-600 leading-relaxed">{c.value}</p>
                  </div>
                </div>
              </div>

              {/* Role */}
              <div className="pt-3 flex items-center justify-between">
                <div>
                  <p className="font-label text-[9px] tracking-[0.12em] uppercase text-foreground-400 mb-1">
                    My Role
                  </p>
                  <p className="text-xs text-foreground-700 leading-relaxed">{c.role}</p>
                </div>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-foreground-950 transition-colors w-5 h-5 flex items-center justify-center" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}